/**
 * Hex grid math — "odd-r" offset coordinates (pointy-top hexes, rows offset).
 * Battlefield: `cols` x `rows` rectangle; each team deploys on `rowsPerSide` rows.
 * Team B occupies rows 0..rowsPerSide-1 (top, backline = row 0),
 * Team A occupies rows rowsPerSide..2*rowsPerSide-1 (bottom, backline = last row).
 * All math is integer-only (CLAUDE.md rule 2).
 */
export interface HexPos {
  col: number
  row: number
}

/** odd-r offset -> cube coords (x, z); y = -x-z implied */
function toCubeX(col: number, row: number): number {
  return col - ((row - (row & 1)) >> 1)
}

export function hexDistance(a: HexPos, b: HexPos): number {
  const ax = toCubeX(a.col, a.row)
  const az = a.row
  const bx = toCubeX(b.col, b.row)
  const bz = b.row
  const dx = ax - bx
  const dz = az - bz
  const dy = -dx - dz
  return (Math.abs(dx) + Math.abs(dy) + Math.abs(dz)) >> 1
}

// Fixed neighbor order (determinism): E, W, NE, NW, SE, SW
const NEIGHBORS_EVEN: ReadonlyArray<readonly [number, number]> = [
  [1, 0], [-1, 0], [0, -1], [-1, -1], [0, 1], [-1, 1],
]
const NEIGHBORS_ODD: ReadonlyArray<readonly [number, number]> = [
  [1, 0], [-1, 0], [1, -1], [0, -1], [1, 1], [0, 1],
]

export function hexNeighbors(p: HexPos, cols: number, rows: number): HexPos[] {
  const table = (p.row & 1) === 1 ? NEIGHBORS_ODD : NEIGHBORS_EVEN
  const out: HexPos[] = []
  for (const [dc, dr] of table) {
    const col = p.col + dc
    const row = p.row + dr
    if (col >= 0 && col < cols && row >= 0 && row < rows) out.push({ col, row })
  }
  return out
}

export function hexIndex(p: HexPos, cols: number): number {
  return p.row * cols + p.col
}

export function hexFromIndex(idx: number, cols: number): HexPos {
  return { col: idx % cols, row: (idx / cols) | 0 }
}

export function samePos(a: HexPos, b: HexPos): boolean {
  return a.col === b.col && a.row === b.row
}
